// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { fetchGet } from '@/api/http';
import { create } from 'zustand';

export type ExecutionMode = 'camel' | 'claude_code';

const MODES: readonly ExecutionMode[] = ['camel', 'claude_code'];
const STORAGE_KEY = 'hanggent_execution_mode_pref';

const isMode = (value: unknown): value is ExecutionMode =>
  typeof value === 'string' && (MODES as readonly string[]).includes(value);

function readPreference(): ExecutionMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isMode(saved) ? saved : 'camel';
  } catch {
    return 'camel';
  }
}

interface ExecutionModeState {
  /** What the server offers; CAMEL until the server says otherwise. */
  available: ExecutionMode[];
  /** What the user last chose; may not be offered by this server. */
  preference: ExecutionMode;
  load: () => Promise<void>;
  setPreference: (mode: ExecutionMode) => void;
  /** The mode a request should use: the preference when the server offers it. */
  effectiveMode: () => ExecutionMode;
}

export const useExecutionModeStore = create<ExecutionModeState>()(
  (set, get) => ({
    available: ['camel'],
    preference: readPreference(),

    load: async () => {
      try {
        const answer = await fetchGet('/chat/execution-modes');
        const offered = Array.isArray(answer?.modes)
          ? answer.modes.filter(isMode)
          : [];
        set({
          available: offered.includes('camel') ? offered : ['camel', ...offered],
        });
      } catch {
        // Not knowing what the server offers means offering only CAMEL.
        set({ available: ['camel'] });
      }
    },

    setPreference: (mode) => {
      set({ preference: mode });
      try {
        localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        // The choice still holds for this session.
      }
    },

    effectiveMode: () => {
      const { preference, available } = get();
      return preference === 'claude_code' && available.includes('claude_code')
        ? 'claude_code'
        : 'camel';
    },
  })
);

export const getExecutionModeForRequest = (): ExecutionMode =>
  useExecutionModeStore.getState().effectiveMode();
